import { useEffect, useMemo, useState } from 'react';

import { OptionType } from '@evinced-private/ui-common';

import { IWebSdkTest } from 'src/apps/web-sdks/types/WebSdkDashboardServiceTypes';

import { markOptionsWithWarningsFromKeyValue } from '../../../helpers/filter-section-v2';
import { useConfiguredValues } from '../../../hooks/useConfiguredValues';
import { extractLabelKey } from '../../shared/helpers';
import { LabelRemoval } from '../../shared/types';
import { extractExistingLabels } from '../helpers/RemoveLabelTestsModalHelpers';

interface UseLabelsRemovalProps {
	testRows: IWebSdkTest[];
}

interface UseLabelsRemovalReturn {
	selectedLabel: OptionType[];
	setSelectedLabel: (label: OptionType[]) => void;
	removals: LabelRemoval[];
	labelOptions: OptionType[];
	removeRemoval: (index: number) => void;
	resetRemovals: () => void;
}

export const useLabelsRemoval = ({ testRows }: UseLabelsRemovalProps): UseLabelsRemovalReturn => {
	const [selectedLabel, setSelectedLabel] = useState<OptionType[]>([]);
	const [removals, setRemovals] = useState<LabelRemoval[]>([]);
	const configuredValues = useConfiguredValues();

	// Extract all existing labels from test rows and apply warnings
	const labelOptions = useMemo((): OptionType[] => {
		const options = extractExistingLabels(testRows);
		return markOptionsWithWarningsFromKeyValue(options, configuredValues, extractLabelKey);
	}, [testRows, configuredValues]);

	// Auto-append the selected label to removals, then reset selection
	useEffect(() => {
		if (selectedLabel.length > 0) {
			const next = selectedLabel[0];
			setRemovals((prev) => {
				if (prev.some((r) => r.label.value === next.value)) {
					return prev;
				}
				return [...prev, { label: next }];
			});
			setSelectedLabel([]);
		}
	}, [selectedLabel]);

	const removeRemoval = (index: number): void => {
		setRemovals((prev) => prev.filter((_, i) => i !== index));
	};

	const resetRemovals = (): void => {
		setRemovals([]);
		setSelectedLabel([]);
	};

	return {
		selectedLabel,
		setSelectedLabel,
		removals,
		labelOptions,
		removeRemoval,
		resetRemovals
	};
};
